/**
 * Display formatting. No I/O, no React — importable from a test without a DOM.
 */

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const DATE = new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' });

const DATE_TIME = new Intl.DateTimeFormat('en', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/**
 * "Tue, Aug 26" — a weekday-first day label, with the year only when it is not
 * this one.
 *
 * The weekday is what makes it useful rather than decorative: the event picker
 * lists several rows that can share a name ("Founders dinner", "1:1 with Sam"),
 * and the way a person recalls which one they mean is "the Tuesday one", not
 * "the 26th". The year is dropped inside the current year because it is noise on
 * every row that has it, and kept outside it because "Aug 26" two years running
 * is exactly the ambiguity this label exists to remove.
 */
const DAY = new Intl.DateTimeFormat('en', { weekday: 'short', month: 'short', day: 'numeric' });

const DAY_WITH_YEAR = new Intl.DateTimeFormat('en', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

/**
 * "3 days ago" / "in 2 hours". Returns `null` — never "never" or "—" — for a
 * missing timestamp, so the caller decides what absence means in its column.
 */
export function relativeTime(
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;

  const delta = at - now.getTime();
  const magnitude = Math.abs(delta);
  if (magnitude < 45_000) return 'just now';

  for (const [unit, ms] of UNITS) {
    if (magnitude >= ms) {
      return RELATIVE.format(Math.round(delta / ms), unit);
    }
  }
  return RELATIVE.format(Math.round(delta / 1000), 'second');
}

export function formatDate(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? null : DATE.format(at);
}

export function formatDateTime(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? null : DATE_TIME.format(at);
}

/**
 * The day label for an event row. See `DAY` above for why it leads with the
 * weekday. Returns `null` for a dateless event so the caller decides what an
 * undated event says — an event with no date is a real thing here (a conference
 * nobody remembers the hour of), not a missing value.
 */
export function formatEventDay(
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  const sameYear = new Date(at).getFullYear() === now.getFullYear();
  return (sameYear ? DAY : DAY_WITH_YEAR).format(at);
}

/** `firstName lastName`, collapsed. A contact with no last name is not "X null". */
export function fullName(first: string, last?: string | null): string {
  return [first, last ?? '']
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .join(' ');
}

export function titleCase(value: string): string {
  return value.length === 0 ? value : value[0]?.toUpperCase() + value.slice(1);
}
